import { NgClass, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ApplicationRef, Component, effect, inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import Lenis from 'lenis';
import { FormsModule } from '@angular/forms';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NgProgressbar } from 'ngx-progressbar';
import { NgProgressRouter } from 'ngx-progressbar/router';
import { filter, first, takeUntil } from 'rxjs';
import { Subject } from 'rxjs';
import { HeaderComponent } from './shared/components/header/header.component';
import { HairstylesService } from './shared/services/hairstyles/hairstyles.service';
import { SocketsService } from './shared/services/sockets/sockets.service';
import { StorageService } from './core/services/storage/storage.service';
import { Footer } from "./shared/components/footer/footer";
import { CartDrawerComponent } from "./features/shop/components/cart-drawer/cart-drawer.component";
import { ChatPanelComponent } from "./shared/components/chat/chat-panel/chat-panel.component";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, HeaderComponent, NgProgressbar, NgProgressRouter, NgClass, FormsModule, Footer, CartDrawerComponent, ChatPanelComponent]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  #hairstylesService: HairstylesService = inject(HairstylesService);
  #appRouter: Router = inject(Router);
  #socketService: SocketsService = inject(SocketsService);
  #storageService: StorageService = inject(StorageService);
  #platformId = inject(PLATFORM_ID);
  #ngZone: NgZone = inject(NgZone);
  #appRef: ApplicationRef = inject(ApplicationRef);
  #destroy$ = new Subject<void>();
  #lenis: Lenis | null = null;
  #meta: Meta = inject(Meta);

  @ViewChild('appProgress') private appProgress?: NgProgressbar;

  isAuthPage: boolean = false;

  constructor() {
    // Utiliser window.location.pathname sur le client pour une détection instantanée
    // car le router peut ne pas être encore prêt au moment du constructeur
    if (isPlatformBrowser(this.#platformId)) {
      this.isAuthPage = window.location.pathname.includes('/auth');
    }

    this.#appRouter.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isAuthPage = event.url.includes('/auth');

        this.#meta.updateTag({ property: 'og:type', content: 'website' });
        this.#meta.updateTag({ 
          property: 'og:url', 
          content: `https://braidsbook.com${event.urlAfterRedirects}` 
        });
      }
    });

    effect((): void => {
      this.#hairstylesService.getHairstyles().subscribe();
    });
  }

  ngOnInit(): void {
    // Connecter le socket pour TOUS les visiteurs (présence/sessions),
    // pas uniquement les coiffeurs — le comptage "users en ligne" en dépend
    this.#socketService.connect()
      .catch(err => console.error('Erreur connexion WebSocket:', err));

    const today = new Date().toISOString().split('T')[0];
    const lastVisit = this.#storageService.getItem('HAS_VISITED');

    if (lastVisit !== today) {
      // Ne pas bloquer, ignorer la promesse
      this.#socketService.emitUserEvent('visit', 'increment')
        .catch(err => console.error('Erreur emit user event:', err));
      this.#storageService.setItem('HAS_VISITED', today);
    }

    // Lenis nécessite le DOM — uniquement côté client
    if (isPlatformBrowser(this.#platformId)) {
      this.#lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 2,
        infinite: false,
        autoResize: true,
      });

      // La boucle tourne indéfiniment (60x/s) : hors de la zone Angular pour
      // ne pas empêcher ApplicationRef.isStable() de se stabiliser (NG0506,
      // l'hydratation attend cette stabilisation et time out sinon).
      this.#ngZone.runOutsideAngular(() => {
        const raf = (time: number) => {
          this.#lenis!.raf(time);
          requestAnimationFrame(raf);
        };

        requestAnimationFrame(raf);
      });

      // Gérer le scroll restoration et les fragments avec Lenis
      this.#appRouter.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntil(this.#destroy$)
        )
        .subscribe((event: NavigationEnd) => {
          const urlTree = this.#appRouter.parseUrl(event.url);
          const fragment = urlTree.fragment;

          if (fragment && this.#lenis) {
            // Attendre que le DOM soit mis à jour
            setTimeout(() => {
              const element = document.getElementById(fragment);
              if (element) {
                // Utiliser Lenis pour un scroll smooth vers le fragment
                this.#lenis!.scrollTo(element, {
                  duration: 1.2,
                  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                  offset: -100 // Offset pour le header fixe
                });
              }
            }, 100);
          } else if (this.#lenis) {
            // Pas de fragment : scroll vers le haut
            // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Double RAF pour s'assurer que le layout est complet
                this.#lenis!.scrollTo(0, {
                  duration: 0.4,
                  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                  immediate: false
                });
              });
            });
          }
        });
    }
  }

  ngAfterViewInit(): void {
    // Affiche la barre de progression pendant le démarrage (hydratation +
    // premières données), et la termine dès qu'Angular est stable — inutile
    // côté serveur où il n'y a personne pour la voir.
    if (isPlatformBrowser(this.#platformId)) {
      this.appProgress?.progressRef.start();

      this.#appRef.isStable
        .pipe(first((stable) => stable), takeUntil(this.#destroy$))
        .subscribe(() => this.appProgress?.progressRef.complete());
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
    if (this.#lenis) {
      this.#lenis.destroy();
    }
  }
}
